import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement } from 'lit/decorators.js';
import * as TagNames from './session-timeout.tagnames';
import { DialogSizes } from '../dialog/dialog.types';
import { ButtonEnums } from '../button';
import { AtlSessionTimeoutProps } from './session-timeout.props';
import { throttle } from '../../throttle';
import { SessionTimeoutEvent } from './session-timeout.events';

@customElement(TagNames.SessionTimeout)
export class AtlSessionTimeout
   extends LitElement
   implements AtlSessionTimeoutProps
{
   declare logoutUrl: string;
   declare inactivityTimeoutMinutes: number;
   declare expiredTimeoutAfterInactivityMinutes: number;
   declare autoLogoutDelayInSeconds: number;

   declare inactivityTimer?: ReturnType<typeof setTimeout>;
   declare sessionExpiredTimer?: ReturnType<typeof setTimeout>;

   declare _showInactivityWarning: boolean;
   declare _showSessionExpired: boolean;

   static properties = {
      logoutUrl: { attribute: 'logout-url', type: String },
      inactivityTimeoutMinutes: {
         attribute: 'inactivity-timeout-minutes',
         type: Number,
      },
      expiredTimeoutAfterInactivityMinutes: {
         attribute: 'expired-inactivity-timeout-minutes',
         type: Number,
      },
      autoLogoutDelayInSeconds: {
         attribute: 'auto-logout-delay-seconds',
         type: Number,
      },
      _showInactivityWarning: { type: Boolean, state: true },
      _showSessionExpired: { type: Boolean, state: true },
   };

   static styles = css`
      div[slot='dialogFooter'] {
         margin-top: 6px;
         margin-bottom: 6px;
      }
   `;

   override connectedCallback() {
      super.connectedCallback();

      this.throttledCheckInactivity();
      document.addEventListener('mousemove', this.throttledCheckInactivity);
      document.addEventListener('keypress', this.throttledCheckInactivity);
      document.addEventListener('touchstart', this.throttledCheckInactivity);
      document.addEventListener('touchmove', this.throttledCheckInactivity);
   }

   override disconnectedCallback() {
      document.removeEventListener('touchmove', this.throttledCheckInactivity);
      document.removeEventListener('touchstart', this.throttledCheckInactivity);
      document.removeEventListener('keypress', this.throttledCheckInactivity);
      document.removeEventListener('mousemove', this.throttledCheckInactivity);

      super.disconnectedCallback();
   }

   protected override updated(changedProperties: PropertyValues): void {
      super.updated(changedProperties);
      this._resetInactivityTimer();
   }

   private get _inactivityTimeoutMinutes() {
      return this.inactivityTimeoutMinutes ?? 1;
   }

   private get _expiredTimeoutAfterInactivityMinutes() {
      return this.expiredTimeoutAfterInactivityMinutes ?? 1;
   }

   private get _autoLogoutDelayInSeconds() {
      return this.autoLogoutDelayInSeconds ?? 5;
   }

   private _startInactivityTimer = () => {
      this._clearInactivityTimer();
      this.inactivityTimer = setTimeout(
         this._showInactivityModal,
         this._inactivityTimeoutMinutes * 60 * 1000
      );
   };

   private _showInactivityModal = () => {
      this._showInactivityWarning = true;
      this._startSessionExpiredTimer();
   };

   private _hideInactivityModal = () => {
      this._showInactivityWarning = false;
      this._resetInactivityTimer();
   };

   private _clearInactivityTimer = () => {
      if (!this.inactivityTimer) return;

      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = undefined;
   };

   private _startSessionExpiredTimer = () => {
      this._clearSessionExpiredTimer();
      this.sessionExpiredTimer = setTimeout(
         this._showSessionExpiredModal,
         this._expiredTimeoutAfterInactivityMinutes * 60 * 1000
      );
   };

   private _showSessionExpiredModal = () => {
      this._showInactivityWarning = false;
      this._showSessionExpired = true;

      setTimeout(() => {
         this._sessionExpired();
      }, this._autoLogoutDelayInSeconds * 1000);
   };

   private _clearSessionExpiredTimer = () => {
      if (!this.sessionExpiredTimer) return;

      clearTimeout(this.sessionExpiredTimer);
      this.sessionExpiredTimer = undefined;
   };

   private _sessionExpired = () => {
      if (this.logoutUrl) {
         window.location.href = this.logoutUrl;
      }
      this.dispatchEvent(new SessionTimeoutEvent());
   };

   private _clearTimers = () => {
      this._clearInactivityTimer();
      this._clearSessionExpiredTimer();
   };

   private _resetInactivityTimer = () => {
      this._clearTimers();
      this._startInactivityTimer();
   };

   private throttledCheckInactivity = throttle(
      this._resetInactivityTimer,
      1000,
      { trailing: false }
   );

   render() {
      if (this._showSessionExpired) {
         return html`
            <atlantis-dialog
               dialogTitle="Session Expired"
               size=${DialogSizes.Small}
               display-close-button
               backdrop
               show
               @dialog-close=${this._sessionExpired}
            >
               <div slot="dialogBody">
                  Your session has timed out. Please log in again.
               </div>
               <div slot="dialogFooter">
                  <atlantis-button
                     variant=${ButtonEnums.ButtonVariant.Cta}
                     @click=${this._sessionExpired}
                  >
                     Ok
                  </atlantis-button>
               </div>
            </atlantis-dialog>
         `;
      }

      if (this._showInactivityWarning) {
         return html` <atlantis-dialog
            dialogTitle="Inactivity Warning"
            size=${DialogSizes.Medium}
            display-close-button
            backdrop
            show
            @dialog-close=${this._hideInactivityModal}
         >
            <div slot="dialogBody">
               You have been inactive for more than
               ${this._inactivityTimeoutMinutes} minutes, you will be
               automatically logged off if you are inactive for more than
               ${this._inactivityTimeoutMinutes +
               this._expiredTimeoutAfterInactivityMinutes}
               minutes.
            </div>
            <div slot="dialogFooter">
               <atlantis-button
                  variant=${ButtonEnums.ButtonVariant.Cta}
                  @click=${this._hideInactivityModal}
               >
                  Continue
               </atlantis-button>
            </div>
         </atlantis-dialog>`;
      }

      return nothing;
   }
}
